<template>
    <ProfileHeaderSectionCommon>
        <template #avatar-overlay>
            <ClientOnly>
                <div class="profile-header-section-edit__avatar-mask" data-testid="profile-photo-edit-mode" @click="editAvatar">
                    <VJoyIcon name="camera" color="white" />
                    {{ $t(`profile.picture.cta.${profile?.photo ? 'modify' : 'add'}`) }}
                </div>
            </ClientOnly>
        </template>
        <template #first-indicator>
            <PriceIndicatorEdit
                v-if="profile"
                :price="profile.price.price"
                :price-hidden="profile.price.priceHidden"
                :currency-code="profile.price.currencyCode"
                :is-strategy-profile="isStrategyProfile"
            />
        </template>
        <template #third-indicator>
            <ReviewsRecommendationsIndicatorEdit :appraisals="appraisals" :recommendations="recommendations" />
        </template>
        <template #actions>
            <ClientOnly>
                <VJoyWrapper class="profile-header-section-edit__actions" no-margin column-gap="1" wrap="nowrap">
                    <VJoyTooltip :text="$t('editable.profile.action.see.public')">
                        <VJoyIconButton
                            icon="view-user"
                            icon-position="left"
                            :label="$t('editable.profile.action.see.public')"
                            size="medium"
                            variant="ghost"
                            :href="`/profile/${profile?.seoAlias}?overview`"
                            target="_blank"
                            data-testid="profile-header-see-as-client-cta"
                        />
                    </VJoyTooltip>
                    <ShareProfileDialogEditMode />
                    <VJoyTooltip :text="$t('general.edit')">
                        <VJoyIconButton
                            icon="pencil"
                            icon-position="left"
                            :label="$t('general.edit')"
                            size="medium"
                            variant="ghost"
                            data-testid="profile-header-edit-cta"
                            @click="editHeader"
                        />
                    </VJoyTooltip>
                </VJoyWrapper>
                <template #fallback>
                    <VJoyFormFieldSkeleton size="xsmall" width="152px" />
                </template>
            </ClientOnly>
        </template>
        <template #skills>
            <ClientOnly>
                <SkillsEdit />
                <template #fallback><VJoyBlockSkeleton height="72px" /></template>
            </ClientOnly>
        </template>
    </ProfileHeaderSectionCommon>
    <EditHeaderSection />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {VJoyBlockSkeleton, VJoyIconButton, VJoyFormFieldSkeleton, VJoyIcon, VJoyWrapper, VJoyTooltip} from '@maltjoy/core-vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import ShareProfileDialogEditMode from '~/features/share-profile/ui/ShareProfileDialogEditMode.vue';
    import ProfileHeaderSectionCommon from './ProfileHeaderSectionCommon.vue';
    import EditHeaderSection from '../edit/EditHeaderSection.vue';
    import PriceIndicatorEdit from '../indicators/price/PriceIndicatorEdit.vue';
    import ReviewsRecommendationsIndicatorEdit from '../indicators/reviews-recommendations/ReviewsRecommendationsIndicatorEdit.vue';
    import SkillsEdit from '../SkillsEdit.vue';
    import {useStrategyProfile} from '@/composables/useStrategyProfile';
    import {useProfileStore} from '@/store/profileStore';

    // Stores
    const profileStore = useProfileStore();
    const {appraisals, profile} = storeToRefs(profileStore);
    const {recommendations} = storeToRefs(useRecommendationsStore());

    // Computed values
    const isStrategyProfile = computed(() => useStrategyProfile() || false);

    function editAvatar() {
        eventBus.emit('photo:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }

    function editHeader() {
        eventBus.emit('header:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-header-section-edit {
        &__avatar-mask {
            position: absolute;
            text-align: center;
            width: 100%;
            height: 100%;
            cursor: pointer;
            display: flex;
            color: var(--joy-color-neutral-0);
            flex-direction: column;
            background-color: transparent;
            padding: 15px;
            border: none;
            justify-content: center;
            align-items: center;
            transition: opacity 0.3s ease;
            opacity: 0;
            border-radius: var(--joy-core-radius-4);
            bottom: 0;

            @include mq.screen_xs {
                font-size: var(--joy-font-size-primary-200);
            }

            &:hover {
                background-color: rgba(3, 82, 102, 0.5);
                opacity: 1;
            }
        }
    }
</style>
